import { useState } from "react";
import { toast } from "sonner";
import { DocumentPicker, type PickerDocument } from "@/components/documents/DocumentPicker";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import * as pmRepo from "@/stores/pm/repository";

interface IssueDocumentPickerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  issueId: number;
  buildingId: string;
  /** WHA-3393: lock the picker to the issue's own project so the user
   *  can't browse documents in another project of the building. */
  projectId: string;
  alreadyAttachedIds: string[];
  onAttached: () => void;
}

export function IssueDocumentPicker({
  open,
  onOpenChange,
  issueId,
  buildingId,
  projectId,
  alreadyAttachedIds,
  onAttached,
}: IssueDocumentPickerProps) {
  const { t } = useLanguage();
  const [isAttaching, setIsAttaching] = useState(false);

  const handleSelect = async (docs: PickerDocument[]) => {
    if (docs.length === 0) return;
    setIsAttaching(true);
    try {
      await Promise.all(
        docs.map((d) => pmRepo.addIssueDocument(issueId, Number(d.id)))
      );
      onAttached();
      onOpenChange(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to attach documents");
    } finally {
      setIsAttaching(false);
    }
  };

  return (
    <DocumentPicker
      open={open}
      onOpenChange={onOpenChange}
      buildingId={buildingId}
      projectId={projectId}
      alreadyAttachedIds={alreadyAttachedIds}
      mode="multi"
      onSelect={handleSelect}
      title={t.pm.issues.detail.addFromFranklin}
      description={isAttaching ? "Attaching…" : undefined}
      submitLabel="Attach"
    />
  );
}
